import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw, ThumbsDown, ThumbsUp } from "lucide-react";

type FeedbackRow = {
  id: string;
  message_id: string | null;
  conversation_id: string | null;
  helpful: boolean;
  comment: string | null;
  retrieved_ids: string[] | null;
  created_at: string;
};

type WindowKey = "7d" | "30d";

function normalizeReason(raw: string | null): string {
  const t = (raw ?? "").trim().toLowerCase();
  if (!t) return "(no reason given)";
  return t.replace(/\s+/g, " ").slice(0, 120);
}

export function HelpdeskFeedbackReviewPanel() {
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [win, setWin] = useState<WindowKey>("7d");

  const load = async () => {
    setLoading(true);
    const since = new Date(Date.now() - (win === "7d" ? 7 : 30) * 24 * 3600_000).toISOString();
    const { data, error } = await supabase
      .from("helpdesk_feedback")
      .select("id, message_id, conversation_id, helpful, comment, retrieved_ids, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(1000);
    if (!error) setRows((data ?? []) as FeedbackRow[]);
    setLoading(false);
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [win]);

  const stats = useMemo(() => {
    const total = rows.length;
    const up = rows.filter((r) => r.helpful).length;
    const down = total - up;
    return { total, up, down, rate: total ? up / total : 0 };
  }, [rows]);

  const topDownReasons = useMemo(() => {
    const buckets = new Map<string, { count: number; sample: FeedbackRow }>();
    for (const r of rows) {
      if (r.helpful) continue;
      const key = normalizeReason(r.comment);
      const b = buckets.get(key);
      if (b) b.count += 1;
      else buckets.set(key, { count: 1, sample: r });
    }
    return [...buckets.entries()]
      .map(([reason, { count, sample }]) => ({ reason, count, sample }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 12);
  }, [rows]);

  const lowConfidenceChunks = useMemo(() => {
    // Chunks that appeared in >=2 downvoted answers — likely candidates to
    // rewrite or supplement with a new FAQ entry.
    const counts = new Map<string, number>();
    for (const r of rows) {
      if (r.helpful) continue;
      for (const id of r.retrieved_ids ?? []) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return [...counts.entries()]
      .filter(([, c]) => c >= 2)
      .map(([chunk_id, count]) => ({ chunk_id, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [rows]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            Helpdesk feedback review
            <Badge variant="secondary" className="font-mono">{stats.total}</Badge>
          </CardTitle>
          <CardDescription>
            Weekly triage: top 👎 reasons and low-confidence intents. Use these to draft new FAQ entries, then re-ingest the KB.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden">
            {(["7d", "30d"] as WindowKey[]).map((k) => (
              <button
                key={k}
                onClick={() => setWin(k)}
                className={`px-2.5 py-1 text-xs ${win === k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"}`}
              >
                {k}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="icon-sm" onClick={() => void load()} aria-label="Refresh">
            {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs text-muted-foreground">Helpful rate</div>
            <div className="text-lg font-semibold">{(stats.rate * 100).toFixed(0)}%</div>
          </div>
          <div className="rounded-lg border border-border p-3 flex items-center justify-between">
            <div>
              <div className="text-xs text-muted-foreground">Upvotes</div>
              <div className="text-lg font-semibold">{stats.up}</div>
            </div>
            <ThumbsUp className="size-4 text-primary" />
          </div>
          <div className="rounded-lg border border-border p-3 flex items-center justify-between">
            <div>
              <div className="text-xs text-muted-foreground">Downvotes</div>
              <div className="text-lg font-semibold">{stats.down}</div>
            </div>
            <ThumbsDown className="size-4 text-destructive" />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-2">Top 👎 reasons</h3>
          {topDownReasons.length === 0 ? (
            <p className="text-sm text-muted-foreground">No downvotes in this window.</p>
          ) : (
            <ul className="space-y-1.5">
              {topDownReasons.map(({ reason, count, sample }) => (
                <li key={reason} className="flex items-start gap-2 text-sm">
                  <Badge variant="destructive" className="shrink-0 tabular-nums">{count}</Badge>
                  <div className="min-w-0">
                    <div className="truncate">{reason}</div>
                    <div className="text-[11px] text-muted-foreground">
                      last: {new Date(sample.created_at).toLocaleString()}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-2">Low-confidence chunks (2+ downvotes)</h3>
          {lowConfidenceChunks.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No chunks flagged. Widget must send <code>retrieved_ids</code> with feedback for this to populate.
            </p>
          ) : (
            <ul className="space-y-1 text-sm font-mono">
              {lowConfidenceChunks.map(({ chunk_id, count }) => (
                <li key={chunk_id} className="flex items-center justify-between border border-border rounded px-2 py-1">
                  <span className="truncate">{chunk_id}</span>
                  <Badge variant="secondary">{count}</Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default HelpdeskFeedbackReviewPanel;
